import {
  IconBorderOuter,
  IconBoxAlignBottom,
  IconBoxAlignLeft,
  IconBoxAlignRight,
  IconBoxAlignTop,
  IconCaretRightFilled,
} from '@tabler/icons-react';
import {
  Box,
  createStyles,
  Group,
  NumberInput,
  NumberInputProps,
  Popover,
  rem,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { useRichTextEditorContext } from '@mantine/tiptap';
import Button from 'components/Button';
import { useDisclosure } from '@mantine/hooks';
import { useForm } from '@mantine/form';
import { DropdownControlKind } from 'pages/revisions-module/template-editor/editors/template/toolbar/controls/DropdownControl';
import P2Regular from 'components/typography/P2Regular';
import P4Medium from 'components/typography/P4Medium';
import { useMemo } from 'react';
import type { Editor } from '@tiptap/react';
import ActionIcon from 'components/ActionIcon';

const useStyles = createStyles((theme) => ({
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 110px)',
    gridTemplateRows: 'auto 130px auto',
    gap: rem(12),
    alignItems: 'center',
  },
  cell: {
    alignSelf: 'stretch',
    backgroundColor: theme.colors.gray[1],
    boxSizing: 'border-box',
  },
  icon: {
    display: 'flex',
    gap: rem(2),
    '& svg': {
      strokeWidth: '2.5 !important',
    },
  },
  item: {
    height: rem(36),
    display: 'grid',
    gridTemplateColumns: 'auto 1fr auto',
    gap: rem(8),
    padding: `${rem(8)} ${rem(16)}`,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.colors.gray[1],
    },
    '& svg': {
      width: rem(16),
      height: rem(16),
    },
  },
  chevronIcon: {
    '& path': {
      fill: theme.colors.gray[6],
    },
  },
}));

/**
 * Content of the popover used to set per-side cell border widths.
 */
function CellBorderWidthForm({ editor, onClose }: { editor: Editor; onClose: () => void }) {
  const { classes } = useStyles();

  const initialValues = useMemo(() => {
    const attrs = editor.getAttributes('tableCell');
    const legacyWidth = Number(attrs['data-border-width'] ?? 1);
    return {
      borderWidthTop: Number(attrs['data-border-width-top'] ?? legacyWidth),
      borderWidthBottom: Number(attrs['data-border-width-bottom'] ?? legacyWidth),
      borderWidthLeft: Number(attrs['data-border-width-left'] ?? legacyWidth),
      borderWidthRight: Number(attrs['data-border-width-right'] ?? legacyWidth),
    };
  }, []);

  const form = useForm({ initialValues });

  const borderPreviewStyle = useMemo(
    () => ({
      borderTop:
        form.values.borderWidthTop === 0
          ? '1px dashed var(--mantine-color-gray-4)'
          : `${form.values.borderWidthTop}px solid var(--mantine-color-gray-7)`,
      borderBottom:
        form.values.borderWidthBottom === 0
          ? '1px dashed var(--mantine-color-gray-4)'
          : `${form.values.borderWidthBottom}px solid var(--mantine-color-gray-7)`,
      borderLeft:
        form.values.borderWidthLeft === 0
          ? '1px dashed var(--mantine-color-gray-4)'
          : `${form.values.borderWidthLeft}px solid var(--mantine-color-gray-7)`,
      borderRight:
        form.values.borderWidthRight === 0
          ? '1px dashed var(--mantine-color-gray-4)'
          : `${form.values.borderWidthRight}px solid var(--mantine-color-gray-7)`,
    }),
    [form.values]
  );

  const inputProps: Partial<NumberInputProps> = useMemo(
    () => ({
      size: 'md',
      min: 0,
      max: 10,
      required: true,
      rightSection: <P4Medium c="gray.6">px</P4Medium>,
      styles: { required: { display: 'none' } },
    }),
    []
  );

  const onSubmit = form.onSubmit((values) => {
    editor
      .chain()
      .focus()
      .setCellAttribute('data-border-width-top', String(values.borderWidthTop))
      .setCellAttribute('data-border-width-bottom', String(values.borderWidthBottom))
      .setCellAttribute('data-border-width-left', String(values.borderWidthLeft))
      .setCellAttribute('data-border-width-right', String(values.borderWidthRight))
      .run();
    onClose();
  });

  return (
    <form onSubmit={onSubmit}>
      <Stack spacing={24} p={16}>
        <Text size="sm" weight={500} color="gray.8">
          Orámovania bunky
        </Text>
        <Box className={classes.grid}>
          <span />
          <NumberInput
            {...inputProps}
            data-autofocus
            icon={<IconBoxAlignTop />}
            {...form.getInputProps('borderWidthTop')}
          />
          <span />

          <NumberInput {...inputProps} icon={<IconBoxAlignLeft />} {...form.getInputProps('borderWidthLeft')} />
          <Box className={classes.cell} style={borderPreviewStyle} />
          <NumberInput {...inputProps} icon={<IconBoxAlignRight />} {...form.getInputProps('borderWidthRight')} />

          <span />
          <NumberInput {...inputProps} icon={<IconBoxAlignBottom />} {...form.getInputProps('borderWidthBottom')} />
          <span />
        </Box>

        <Group spacing={16} position="right">
          <Button type="button" onClick={onClose} variant="subtle-gray">
            Zrušiť
          </Button>
          <Button type="submit" variant="primary">
            Nastaviť
          </Button>
        </Group>
      </Stack>
    </form>
  );
}

/**
 * Sets the border width per side for the selected table cell.
 */
export default function CellBorderWidth({ kind }: { kind: DropdownControlKind }) {
  const { editor } = useRichTextEditorContext();
  const [opened, { toggle, close }] = useDisclosure();
  const { classes } = useStyles();

  if (kind === 'action-icon') {
    return (
      <Popover opened={opened} onClose={close} withinPortal position="bottom-start">
        <Popover.Target>
          <Tooltip
            label="Šírka orámovania bunky"
            withArrow={false}
            openDelay={500}
            withinPortal
            position="bottom"
            disabled={opened}
          >
            <ActionIcon variant="subtle-gray" size="xs" className={classes.icon} onClick={toggle}>
              <IconBorderOuter />
            </ActionIcon>
          </Tooltip>
        </Popover.Target>
        <Popover.Dropdown p={0}>{opened && <CellBorderWidthForm editor={editor} onClose={close} />}</Popover.Dropdown>
      </Popover>
    );
  }

  return (
    <Popover opened={opened} onClose={close} withinPortal position="right-start">
      <Popover.Target>
        <Box className={classes.item} onClick={toggle}>
          <Box>
            <IconBorderOuter size={16} />
          </Box>
          <P2Regular c="gray.8">Šírka orámovania bunky</P2Regular>
          <IconCaretRightFilled size={12} className={classes.chevronIcon} />
        </Box>
      </Popover.Target>
      <Popover.Dropdown p={0}>{opened && <CellBorderWidthForm editor={editor} onClose={close} />}</Popover.Dropdown>
    </Popover>
  );
}
